import type { AxiosResponse } from "axios"

import getCurrentLocale from "@/utils/getCurrentLocale"
import { PM_API } from "@/utils/http"

import type * as Contracts from "../types/productModeration"

type PaginatedQuery<T> = T & { page?: number; pageSize?: number }
type ListRequest<T> = {
  queryParams: PaginatedQuery<T>
  signal?: AbortSignal
}
const paginate = <T>(query: PaginatedQuery<T>) => {
  const { page = 1, pageSize = 10, ...params } = query
  return { limit: pageSize, offset: (page - 1) * pageSize, ...params }
}
const localeHeaders = () => ({ "Accept-Language": getCurrentLocale() })

export const fetchProductDrafts = (params: Contracts.ProductDraftListQuery = {}) =>
  PM_API.get<Contracts.ProductDraftListResponse>("/products/drafts/", { params })

// The documented POST has no request body. Values are written with PUT afterwards.
export const createProductDraft = () =>
  PM_API.post<Contracts.CreateProductDraftResponse>("/products/drafts/")

export const fetchProductDraft = ({ oid }: { oid: string }) =>
  PM_API.get<Contracts.ProductDraftResponse>(`/products/drafts/${oid}/`)

export const updateProductDraft = ({
  oid,
  payload
}: {
  oid: string
  payload: Contracts.UpdateProductDraftPayload
}) => PM_API.put<Contracts.UpdateProductDraftResponse>(`/products/drafts/${oid}/`, payload)

export const completeProductDraft = ({ oid }: { oid: string }) =>
  PM_API.post<Contracts.CompleteProductDraftResponse>(`/products/drafts/${oid}/complete/`)

export const deleteProductDraft = ({ oid }: Contracts.DeleteProductDraftParams) =>
  PM_API.delete<Contracts.DeleteProductDraftResponse>(
    `/products/drafts/${encodeURIComponent(oid)}/`,
    { customConfig: { ignoreInterceptor: true } }
  )

export const fetchProductTypes = async ({
  queryParams,
  signal
}: ListRequest<Contracts.ProductTypesQuery>): Promise<
  Pick<AxiosResponse<Contracts.ProductTypesResponse>, "data">
> => {
  if (!queryParams.related_category) return { data: { results: [], count: 0 } }

  return PM_API.get<Contracts.ProductTypesResponse>("/product-types/", {
    params: paginate(queryParams),
    headers: localeHeaders(),
    signal
  })
}

export const fetchAttributesByType = ({ code }: { code: string }) =>
  PM_API.get<Contracts.TypeAttributesResponse>(`/product-types/${code}/attributes/`, {
    params: { order_by: "order" } satisfies Contracts.TypeAttributesQuery
  })

export const fetchProductBrands = ({
  queryParams,
  signal
}: ListRequest<Contracts.ProductBrandsQuery>) =>
  PM_API.get<Contracts.ProductBrandsResponse>("/brands/", {
    params: paginate(queryParams),
    headers: localeHeaders(),
    signal
  })

export const createBrand = ({ payload }: { payload: Contracts.CreateBrandPayload }) =>
  PM_API.post<Contracts.CreateBrandResponse>("/brands/", payload)

export const fetchProductAttributes = ({
  queryParams,
  signal
}: ListRequest<Contracts.ProductAttributesQuery>) =>
  PM_API.get<Contracts.ProductAttributesResponse>("/attributes/", {
    params: paginate(queryParams),
    headers: localeHeaders(),
    signal
  })

export const createAttribute = ({ payload }: { payload: Contracts.CreateAttributePayload }) =>
  PM_API.post<Contracts.CreateAttributeResponse>("/attributes/", payload, {
    headers: localeHeaders()
  })

export const fetchAttributeValues = ({
  queryParams,
  signal
}: ListRequest<Contracts.AttributeValuesQuery & { code: string }>) => {
  const { code, ...query } = queryParams
  return PM_API.get<Contracts.AttributeValuesResponse>(`/attributes/${code}/list-values/`, {
    params: paginate(query),
    headers: localeHeaders(),
    signal
  })
}

export const fetchAttributeUnits = ({
  queryParams,
  signal
}: {
  queryParams: { code: string }
  signal?: AbortSignal
}) =>
  PM_API.get<Contracts.AttributeUnitsResponse>(`/attributes/${queryParams.code}/possible-units/`, {
    headers: localeHeaders(),
    signal
  })

export const addNewValueToAttribute = ({
  code,
  payload
}: {
  code: string
  payload: Contracts.CreateAttributeValuePayload
}) =>
  PM_API.post<Contracts.CreateAttributeValueResponse>(`/attributes/${code}/list-values/`, payload, {
    headers: localeHeaders()
  })

export const uploadImageOnCdn = (payload: FormData) =>
  PM_API.post<Contracts.UploadImageResponse>("/images/", payload)

export const translateText = (payload: Contracts.TranslateTextPayload) =>
  PM_API.post<Contracts.TranslateTextResponse>("/ai/translate", payload)
